import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, Strikethrough, Heading2, Heading3, List, ListOrdered,
  ListChecks, Link2, Quote, Undo2, Redo2, Mic, MicOff,
} from 'lucide-react';
import { DictationHint } from './DictationHint';
import { SPEECH_SUPPORTED, tokenizeDictation, applyOpsToEditor } from './dictationCommands';

// Toolbar button
const TB = ({ active, onClick, title, disabled, children, testid }) => (
  <button type="button" title={title} data-testid={testid} disabled={disabled}
    onMouseDown={(e) => { e.preventDefault(); if (!disabled) onClick(); }}
    className={`n-focus inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors disabled:opacity-40
      ${active ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
    {children}
  </button>
);

// ---------------------------------------------------------------------------
// Rich text editor for a task body (body_html) — the same TipTap stack used by
// Notes: headings, lists, checklists and links. Debounced autosave + onBlur.
// Remount per task via a `key={taskId}` from the parent.
// ---------------------------------------------------------------------------
export default function TaskBodyEditor({ initialHtml = '', canEdit = true, onSave }) {
  const saveTimer = useRef(null);
  const lastSaved = useRef(initialHtml || '');

  const flush = useCallback((html) => {
    if (html === lastSaved.current) return;
    lastSaved.current = html;
    onSave?.(html);
  }, [onSave]);

  const editor = useEditor({
    editable: canEdit,
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Placeholder.configure({ placeholder: canEdit ? "Add details, a checklist, or paste a link…" : 'No details.' }),
    ],
    content: initialHtml || '',
    editorProps: { attributes: { class: 'min-h-[120px] focus:outline-none text-[14px]' } },
    onUpdate: ({ editor }) => {
      if (!canEdit) return;
      const html = editor.getHTML();
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => flush(html), 700);
    },
    onBlur: ({ editor }) => {
      if (!canEdit) return;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      flush(editor.getHTML());
    },
  });

  useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current); }, []);
  useEffect(() => { if (editor) editor.setEditable(canEdit); }, [editor, canEdit]);

  // ---- Voice dictation (Web Speech API) — commands + smart spacing ----------
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const recognitionRef = useRef(null);
  const shouldListenRef = useRef(false);

  const insertDictated = useCallback((raw) => {
    if (!editor) return;
    const ops = tokenizeDictation(raw);
    if (!ops.length) return;
    applyOpsToEditor(editor, ops);
  }, [editor]);

  const stopDictation = useCallback(() => {
    shouldListenRef.current = false;
    setListening(false);
    setInterim('');
    try { recognitionRef.current && recognitionRef.current.stop(); } catch { /* noop */ }
  }, []);

  const startDictation = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR || !editor || !canEdit) return;
    let rec = recognitionRef.current;
    if (!rec) {
      rec = new SR();
      rec.lang = 'en-US';
      rec.continuous = true;
      rec.interimResults = true;
      rec.onresult = (e) => {
        let finalText = '';
        let interimText = '';
        for (let i = e.resultIndex; i < e.results.length; i += 1) {
          const seg = e.results[i];
          if (seg.isFinal) finalText += seg[0].transcript;
          else interimText += seg[0].transcript;
        }
        if (finalText) insertDictated(finalText);
        setInterim(interimText);
      };
      rec.onerror = (ev) => {
        if (ev.error === 'not-allowed' || ev.error === 'service-not-allowed') {
          shouldListenRef.current = false;
          setListening(false);
          setInterim('');
        }
      };
      rec.onend = () => {
        setInterim('');
        if (shouldListenRef.current) { try { rec.start(); } catch { /* noop */ } }
        else setListening(false);
      };
      recognitionRef.current = rec;
    }
    shouldListenRef.current = true;
    try { rec.start(); setListening(true); } catch { /* already started */ }
  }, [editor, canEdit, insertDictated]);

  const toggleDictation = useCallback(() => {
    if (listening) stopDictation(); else startDictation();
  }, [listening, startDictation, stopDictation]);

  useEffect(() => () => { shouldListenRef.current = false; try { recognitionRef.current && recognitionRef.current.abort(); } catch { /* noop */ } }, []);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes('link')?.href || '';
    const url = window.prompt('Link URL', prev);
    if (url === null) return;
    if (url === '') editor.chain().focus().extendMarkRange('link').unsetLink().run();
    else editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="rounded-xl border border-[var(--n-border)] bg-[var(--n-canvas-2)]" data-testid="task-body-editor">
      {canEdit && (
        <div className="flex flex-wrap items-center gap-0.5 border-b border-[var(--n-border)] px-1.5 py-1">
          <TB title="Bold" testid="task-body-bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={15} /></TB>
          <TB title="Italic" testid="task-body-italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={15} /></TB>
          <TB title="Strikethrough" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough size={15} /></TB>
          <span className="mx-1 h-5 w-px bg-[var(--n-border)]" />
          <TB title="Heading" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={15} /></TB>
          <TB title="Subheading" active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 size={15} /></TB>
          <span className="mx-1 h-5 w-px bg-[var(--n-border)]" />
          <TB title="Bullet list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={15} /></TB>
          <TB title="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={15} /></TB>
          <TB title="Checklist" testid="task-body-checklist" active={editor.isActive('taskList')} onClick={() => editor.chain().focus().toggleTaskList().run()}><ListChecks size={15} /></TB>
          <TB title="Quote" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote size={15} /></TB>
          <TB title="Link" testid="task-body-link" active={editor.isActive('link')} onClick={setLink}><Link2 size={15} /></TB>
          <span className="mx-1 h-5 w-px bg-[var(--n-border)]" />
          <TB title="Undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}><Undo2 size={15} /></TB>
          <TB title="Redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}><Redo2 size={15} /></TB>
          {SPEECH_SUPPORTED && (
            <>
              <span className="mx-1 h-5 w-px bg-[var(--n-border)]" />
              <TB title={listening ? 'Stop dictation' : 'Dictate (voice to text)'} testid="task-body-dictate-button"
                active={listening} onClick={toggleDictation}>
                {listening ? <MicOff size={15} /> : <Mic size={15} />}
              </TB>
              <DictationHint side="bottom" align="start" />
              {listening && (
                <span className="ml-1 inline-flex max-w-[200px] items-center gap-1.5 rounded-full bg-[var(--n-danger,#e5484d)]/10 px-2 py-0.5 text-[11px] font-medium text-[var(--n-danger,#e5484d)]" data-testid="task-body-dictate-status">
                  <span className="relative flex h-2 w-2 shrink-0">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-60" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-current" />
                  </span>
                  <span className="truncate">{interim ? interim : 'Listening…'}</span>
                </span>
              )}
            </>
          )}
        </div>
      )}
      <EditorContent editor={editor} className="px-3 py-2.5" data-testid="task-body-content" />
    </div>
  );
}
